import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Autocomplete,
  Chip,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  LocationOn,
  PhotoCamera,
  Close,
  Work,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import CityAutocomplete from "./CityAutocomplete";
import { registerProfessional } from "../redux/slices/authSlice";

const toSlug = (value) => {
  if (!value) return "";
  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['’]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
};

const FREELANCE_TYPE_OPTIONS = [
  { label: "Agent sportif", slug: "agent_sportif" },
  { label: "Analyste vidéo", slug: "analyste_video" },
  { label: "Animateur sportif", slug: "animateur_sportif" },
  { label: "Autre (à préciser)", slug: "autre" },
  { label: "Chargé de projet sportif", slug: "charge_de_projet_sportif" },
  { label: "Coach mental", slug: "coach_mental" },
  { label: "Coach sportif", slug: "coach_sportif" },
  { label: "Coordinateur sportif", slug: "coordinateur_sportif" },
  { label: "Éducateur sportif", slug: "educateur_sportif" },
  {
    label: "Enseignant en activité physique adaptée (APA)",
    slug: "enseignant_en_activite_physique_adaptee_apa",
  },
  { label: "Entraîneur sportif", slug: "entraineur_sportif" },
  { label: "Formateur sportif", slug: "formateur_sportif" },
  { label: "Manager sportif", slug: "manager_sportif" },
  { label: "Maître-nageur sauveteur", slug: "maitre_nageur_sauveteur" },
  { label: "Médecin du sport", slug: "medecin_du_sport" },
  { label: "Nutritionniste sportif", slug: "nutritionniste_sportif" },
  { label: "Préparateur physique", slug: "preparateur_physique" },
  { label: "Psychologue du sport", slug: "psychologue_du_sport" },
  { label: "Rééducateur sportif", slug: "reeducateur_sportif" },
  {
    label: "Responsable de développement sportif",
    slug: "responsable_de_developpement_sportif",
  },
  {
    label: "Responsable performance sportive",
    slug: "responsable_performance_sportive",
  },
  { label: "Scout / Recruteur sportif", slug: "scout_recruteur_sportif" },
];

const DISCIPLINE_OPTIONS = [
  {
    family: "Sports collectifs",
    label: "Basketball",
    slug: "basketball",
  },
  {
    family: "Sports collectifs",
    label: "Beach soccer",
    slug: "beach_soccer",
  },
  {
    family: "Sports collectifs",
    label: "Beach volley",
    slug: "beach_volley",
  },
  {
    family: "Sports collectifs",
    label: "Football",
    slug: "football",
  },
  {
    family: "Sports collectifs",
    label: "Futsal",
    slug: "futsal",
  },
  {
    family: "Sports collectifs",
    label: "Handball",
    slug: "handball",
  },
  {
    family: "Sports collectifs",
    label: "Hockey sur gazon",
    slug: "hockey_sur_gazon",
  },
  {
    family: "Sports collectifs",
    label: "Rugby à 7",
    slug: "rugby_a_7",
  },
  {
    family: "Sports collectifs",
    label: "Rugby à XIII",
    slug: "rugby_a_xiii",
  },
  {
    family: "Sports collectifs",
    label: "Rugby à XV",
    slug: "rugby_a_xv",
  },
  {
    family: "Sports collectifs",
    label: "Volleyball",
    slug: "volleyball",
  },
  {
    family: "Sports collectifs",
    label: "Water-polo",
    slug: "water_polo",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Aïkido",
    slug: "aikido",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Boxe anglaise",
    slug: "boxe_anglaise",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Boxe française (savate)",
    slug: "boxe_francaise_savate",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Judo",
    slug: "judo",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Karaté",
    slug: "karate",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Kick-boxing",
    slug: "kick_boxing",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Lutte",
    slug: "lutte",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "MMA",
    slug: "mma",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Taekwondo",
    slug: "taekwondo",
  },
  {
    family: "Sports de combat et arts martiaux",
    label: "Viet vo dao",
    slug: "viet_vo_dao",
  },
  {
    family: "Sports de raquette",
    label: "Badminton",
    slug: "badminton",
  },
  {
    family: "Sports de raquette",
    label: "Padel",
    slug: "padel",
  },
  {
    family: "Sports de raquette",
    label: "Squash",
    slug: "squash",
  },
  {
    family: "Sports de raquette",
    label: "Tennis",
    slug: "tennis",
  },
  {
    family: "Sports de raquette",
    label: "Tennis de table",
    slug: "tennis_de_table",
  },
  {
    family: "Sports athlétiques et d’endurance",
    label: "Athlétisme",
    slug: "athletisme",
  },
  {
    family: "Sports athlétiques et d’endurance",
    label: "Biathlon",
    slug: "biathlon",
  },
  {
    family: "Sports athlétiques et d’endurance",
    label: "Course à pied",
    slug: "course_a_pied",
  },
  {
    family: "Sports athlétiques et d’endurance",
    label: "Cross-training",
    slug: "cross_training",
  },
  {
    family: "Sports athlétiques et d’endurance",
    label: "Duathlon",
    slug: "duathlon",
  },
  {
    family: "Sports athlétiques et d’endurance",
    label: "Marathon",
    slug: "marathon",
  },
  {
    family: "Sports athlétiques et d’endurance",
    label: "Triathlon",
    slug: "triathlon",
  },
  {
    family: "Sports aquatiques",
    label: "Aviron",
    slug: "aviron",
  },
  {
    family: "Sports aquatiques",
    label: "Canoë-kayak",
    slug: "canoe_kayak",
  },
  {
    family: "Sports aquatiques",
    label: "Natation",
    slug: "natation",
  },
  {
    family: "Sports aquatiques",
    label: "Plongée sportive",
    slug: "plongee_sportive",
  },
  {
    family: "Sports aquatiques",
    label: "Sauvetage sportif",
    slug: "sauvetage_sportif",
  },
  {
    family: "Sports aquatiques",
    label: "Surf",
    slug: "surf",
  },
  {
    family: "Sports aquatiques",
    label: "Voile",
    slug: "voile",
  },
  {
    family: "Sports de force, fitness et bien-être",
    label: "Bodybuilding",
    slug: "bodybuilding",
  },
  {
    family: "Sports de force, fitness et bien-être",
    label: "CrossFit",
    slug: "crossfit",
  },
  {
    family: "Sports de force, fitness et bien-être",
    label: "Fitness",
    slug: "fitness",
  },
  {
    family: "Sports de force, fitness et bien-être",
    label: "Haltérophilie",
    slug: "haltereophilie",
  },
  {
    family: "Sports de force, fitness et bien-être",
    label: "Musculation",
    slug: "musculation",
  },
  {
    family: "Sports de force, fitness et bien-être",
    label: "Pilates",
    slug: "pilates",
  },
  {
    family: "Sports de force, fitness et bien-être",
    label: "Yoga",
    slug: "yoga",
  },
  {
    family: "Sports de glisse et de nature",
    label: "Escalade",
    slug: "escalade",
  },
  {
    family: "Sports de glisse et de nature",
    label: "Kitesurf",
    slug: "kitesurf",
  },
  {
    family: "Sports de glisse et de nature",
    label: "Randonnée sportive",
    slug: "randonnee_sportive",
  },
  {
    family: "Sports de glisse et de nature",
    label: "Ski alpin",
    slug: "ski_alpin",
  },
  {
    family: "Sports de glisse et de nature",
    label: "Snowboard",
    slug: "snowboard",
  },
  {
    family: "Sports de glisse et de nature",
    label: "Trail",
    slug: "trail",
  },
  {
    family: "Sports de glisse et de nature",
    label: "VTT",
    slug: "vtt",
  },
  {
    family: "Sports de précision et de cible",
    label: "Bowling",
    slug: "bowling",
  },
  {
    family: "Sports de précision et de cible",
    label: "Golf",
    slug: "golf",
  },
  {
    family: "Sports de précision et de cible",
    label: "Tir à l’arc",
    slug: "tir_a_larc",
  },
  {
    family: "Sports de précision et de cible",
    label: "Tir sportif",
    slug: "tir_sportif",
  },
  {
    family: "Sports artistiques et d’expression",
    label: "Danse sportive",
    slug: "danse_sportive",
  },
  {
    family: "Sports artistiques et d’expression",
    label: "Danse urbaine",
    slug: "danse_urbaine",
  },
  {
    family: "Sports artistiques et d’expression",
    label: "Gymnastique artistique",
    slug: "gymnastique_artistique",
  },
  {
    family: "Sports artistiques et d’expression",
    label: "Gymnastique rythmique",
    slug: "gymnastique_rythmique",
  },
  {
    family: "Sports artistiques et d’expression",
    label: "Patinage artistique",
    slug: "patinage_artistique",
  },
  {
    family: "Sports inclusifs et adaptés",
    label: "Activité physique adaptée (APA)",
    slug: "activite_physique_adaptee_apa",
  },
  {
    family: "Sports inclusifs et adaptés",
    label: "Handisport",
    slug: "handisport",
  },
  {
    family: "Sports inclusifs et adaptés",
    label: "Sport adapté",
    slug: "sport_adapte",
  },
  {
    family: "Sports inclusifs et adaptés",
    label: "Sport santé",
    slug: "sport_sante",
  },
  {
    family: "Autre",
    label: "Autre discipline (à préciser)",
    slug: "autre",
  },
];

const ProfessionalRegisterForm = ({ loading }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    location: "",
    freelanceTypes: [],
    otherFreelanceType: "",
    disciplines: [],
    otherDiscipline: "",
    profilePicture: null,
    acceptTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [profilePreview, setProfilePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDisciplinesChange = (event, newValue) => {
    setFormData((prev) => ({ ...prev, disciplines: newValue }));
    if (errors.disciplines) {
      setErrors((prev) => ({ ...prev, disciplines: "" }));
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        setErrors((prev) => ({
          ...prev,
          profilePicture: "La taille du fichier ne doit pas dépasser 5MB",
        }));
        return;
      }

      setFormData((prev) => ({ ...prev, profilePicture: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Clear error
      if (errors.profilePicture) {
        setErrors((prev) => ({ ...prev, profilePicture: "" }));
      }
    }
  };

  const removeProfilePicture = () => {
    setFormData((prev) => ({ ...prev, profilePicture: null }));
    setProfilePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Le prénom est requis";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Le nom est requis";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (!formData.password) {
      newErrors.password = "Le mot de passe est requis";
    } else if (formData.password.length < 6) {
      newErrors.password =
        "Le mot de passe doit contenir au moins 6 caractères";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Le téléphone est requis";
    }

    if (!formData.location.trim()) {
      newErrors.location = "La localisation est requise";
    }

    if (
      !Array.isArray(formData.freelanceTypes) ||
      formData.freelanceTypes.length === 0
    ) {
      newErrors.freelanceTypes =
        "Veuillez sélectionner au moins un type de freelance";
    } else if (
      formData.freelanceTypes.some((t) => t.slug === "autre") &&
      !formData.otherFreelanceType.trim()
    ) {
      newErrors.otherFreelanceType = "Veuillez préciser le type de freelance";
    }

    if (formData.disciplines.length === 0) {
      newErrors.disciplines = "Veuillez sélectionner au moins une discipline";
    } else if (
      formData.disciplines.some((d) => d.slug === "autre") &&
      !formData.otherDiscipline.trim()
    ) {
      newErrors.otherDiscipline = "Veuillez préciser la discipline";
    }

    if (!formData.profilePicture) {
      newErrors.profilePicture = "La photo de profil est obligatoire";
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms =
        "Vous devez accepter les conditions d'utilisation";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm()) {
      const selectedTypeLabels = formData.freelanceTypes
        .filter((t) => t.slug !== "autre")
        .map((t) => t.label);

      const otherTypeLabel = formData.freelanceTypes.some(
        (t) => t.slug === "autre",
      )
        ? formData.otherFreelanceType.trim()
        : "";

      const finalTypeLabels = otherTypeLabel
        ? [...selectedTypeLabels, otherTypeLabel]
        : selectedTypeLabels;

      const finalTypeSlugs = [
        ...formData.freelanceTypes
          .filter((t) => t.slug !== "autre")
          .map((t) => t.slug),
        ...(otherTypeLabel ? [toSlug(otherTypeLabel)] : []),
      ];

      const selectedDisciplineLabels = formData.disciplines
        .filter((d) => d.slug !== "autre")
        .map((d) => d.label);

      const otherDisciplineLabel = formData.disciplines.some(
        (d) => d.slug === "autre",
      )
        ? formData.otherDiscipline.trim()
        : "";

      const finalDisciplineLabels = otherDisciplineLabel
        ? [...selectedDisciplineLabels, otherDisciplineLabel]
        : selectedDisciplineLabels;

      const finalDisciplineSlugs = [
        ...formData.disciplines
          .filter((d) => d.slug !== "autre")
          .map((d) => d.slug),
        ...(otherDisciplineLabel ? [toSlug(otherDisciplineLabel)] : []),
      ];

      const submissionData = {
        ...formData,
        freelanceType: finalTypeLabels[0] || otherTypeLabel || "",
        freelanceTypeLabels: finalTypeLabels,
        freelanceTypes: finalTypeSlugs,
        disciplines: finalDisciplineLabels,
        disciplineSlugs: finalDisciplineSlugs,
      };

      delete submissionData.otherFreelanceType;
      delete submissionData.otherDiscipline;

      dispatch(registerProfessional(submissionData));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} className="register-form">
      <Box className="form-row">
        <TextField
          fullWidth
          label="Prénom"
          value={formData.firstName}
          onChange={handleInputChange("firstName")}
          error={!!errors.firstName}
          helperText={errors.firstName}
          placeholder="Votre prénom"
          InputProps={{
            startAdornment: <Person sx={{ color: "action.active", mr: 1 }} />,
          }}
          className="form-field"
        />

        <TextField
          fullWidth
          label="Nom"
          value={formData.lastName}
          onChange={handleInputChange("lastName")}
          error={!!errors.lastName}
          helperText={errors.lastName}
          placeholder="Votre nom"
          InputProps={{
            startAdornment: <Person sx={{ color: "action.active", mr: 1 }} />,
          }}
          className="form-field"
        />
      </Box>

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange("email")}
        error={!!errors.email}
        helperText={errors.email}
        placeholder="votre.email@exemple.com"
        InputProps={{
          startAdornment: <Email sx={{ color: "action.active", mr: 1 }} />,
        }}
        className="form-field"
      />

      <Box className="form-row">
        <TextField
          fullWidth
          label="Mot de passe"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleInputChange("password")}
          error={!!errors.password}
          helperText={errors.password}
          placeholder="Minimum 6 caractères"
          className="form-field"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirmer le mot de passe"
          type={showConfirmPassword ? "text" : "password"}
          value={formData.confirmPassword}
          onChange={handleInputChange("confirmPassword")}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          placeholder="Répétez votre mot de passe"
          className="form-field"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className="form-row">
        <TextField
          fullWidth
          label="Téléphone"
          value={formData.phone}
          onChange={handleInputChange("phone")}
          error={!!errors.phone}
          helperText={errors.phone}
          placeholder="06 12 34 56 78"
          InputProps={{
            startAdornment: <Phone sx={{ color: "action.active", mr: 1 }} />,
          }}
          className="form-field"
        />

        <CityAutocomplete
          value={formData.location}
          onChange={(newValue) => {
            setFormData((prev) => ({ ...prev, location: newValue }));
            if (errors.location) {
              setErrors((prev) => ({ ...prev, location: "" }));
            }
          }}
          error={!!errors.location}
          helperText={errors.location}
          className="form-field"
        />
      </Box>

      <Autocomplete
        multiple
        options={FREELANCE_TYPE_OPTIONS}
        value={formData.freelanceTypes}
        onChange={(event, newValue) => {
          setFormData((prev) => ({ ...prev, freelanceTypes: newValue || [] }));
          if (errors.freelanceTypes) {
            setErrors((prev) => ({ ...prev, freelanceTypes: "" }));
          }
          if (
            !newValue?.some((t) => t.slug === "autre") &&
            formData.otherFreelanceType
          ) {
            setFormData((prev) => ({ ...prev, otherFreelanceType: "" }));
            if (errors.otherFreelanceType) {
              setErrors((prev) => ({ ...prev, otherFreelanceType: "" }));
            }
          }
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.slug === value.slug}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option.label}
              {...getTagProps({ index })}
              key={option.slug}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Type de freelance"
            placeholder="Sélectionnez un ou plusieurs types"
            error={!!errors.freelanceTypes}
            helperText={
              errors.freelanceTypes || "Choisissez une ou plusieurs spécialités"
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Work sx={{ color: "action.active", mr: 1 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        className="form-field"
      />

      {formData.freelanceTypes.some((t) => t.slug === "autre") && (
        <TextField
          fullWidth
          label="Précisez votre type de freelance"
          value={formData.otherFreelanceType}
          onChange={handleInputChange("otherFreelanceType")}
          error={!!errors.otherFreelanceType}
          helperText={errors.otherFreelanceType || "Indiquez votre spécialité"}
          placeholder="Ex: Directeur technique, Préparateur mental..."
          className="form-field"
        />
      )}

      <Autocomplete
        multiple
        options={DISCIPLINE_OPTIONS}
        value={formData.disciplines}
        onChange={handleDisciplinesChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.slug === value.slug}
        groupBy={(option) => option.family}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option.label}
              {...getTagProps({ index })}
              key={option.slug}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Disciplines sportives"
            placeholder="Sélectionnez vos spécialités"
            error={!!errors.disciplines}
            helperText={
              errors.disciplines || "Choisissez une ou plusieurs disciplines"
            }
            className="disciplines-field"
          />
        )}
        className="form-field"
      />

      {formData.disciplines.some((d) => d.slug === "autre") && (
        <TextField
          fullWidth
          label="Précisez la discipline"
          value={formData.otherDiscipline}
          onChange={handleInputChange("otherDiscipline")}
          error={!!errors.otherDiscipline}
          helperText={errors.otherDiscipline || "Indiquez votre discipline"}
          placeholder="Ex: Pétanque, Escrime..."
          className="form-field"
        />
      )}

      <Box className="form-field">
        <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
          Photo de profil <span style={{ color: "#d32f2f" }}>*</span>
        </Typography>

        <Box
          className={`file-upload-container ${formData.profilePicture ? "has-file" : ""}`}
          onClick={() => document.getElementById("profile-upload").click()}
        >
          <input
            id="profile-upload"
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />

          {profilePreview ? (
            <Box className="file-preview">
              <img src={profilePreview} alt="Aperçu" />
              <Box>
                <Typography variant="body2" color="primary">
                  {formData.profilePicture?.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeProfilePicture();
                  }}
                  sx={{ mt: 0.5 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <>
              <PhotoCamera
                sx={{ fontSize: 48, color: "action.active", mb: 1 }}
              />
              <Typography variant="body2" className="file-upload-text">
                Cliquez pour ajouter une photo
              </Typography>
              <Typography variant="caption" className="file-upload-text">
                JPG, PNG - Max 5MB
              </Typography>
            </>
          )}
        </Box>

        {errors.profilePicture && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: "block" }}
          >
            {errors.profilePicture}
          </Typography>
        )}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.acceptTerms}
            onChange={handleInputChange("acceptTerms")}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            J'accepte les{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              conditions d'utilisation
            </a>{" "}
            et la{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              politique de confidentialité
            </a>
          </Typography>
        }
        className="terms-checkbox"
        sx={{ alignItems: "flex-start" }}
      />

      {errors.acceptTerms && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          {errors.acceptTerms}
        </Typography>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading}
        className="register-button"
        startIcon={
          loading ? <CircularProgress size={20} color="inherit" /> : null
        }
      >
        {loading ? "Inscription en cours..." : "S'inscrire comme professionnel"}
      </Button>
    </Box>
  );
};

export default ProfessionalRegisterForm;
